import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// @@SITE_ANALYTICS_v92@@ HQ › Website › "Traffic" tab.
// First-party page views for xyzmerch.com and the public vendor links
// (GET /api/site/analytics), plus the Google Analytics ID, which is part of the
// site DRAFT like every other field (Save draft → Publish makes it live).
// "Visitors" = one browser counted once per day — the server rotates its
// anonymous visitor hash daily and stores no IPs.

const S = {
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em' },
  body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  hint: { margin: 0, color: 'var(--hq-muted)', fontSize: 13, lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' },
  muted: { color: 'var(--hq-muted)', fontSize: 13 },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
};
const BAR = '#2ea847'; // brand green, one step darker so it sits in the dark-mode lightness band
const LINK = { color: '#31b44b', textDecoration: 'none' };
const RANGES = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '12 months']];
const SURFACES = [['all', 'Everything'], ['site', 'Website'], ['vendor', 'Vendor links']];
const GA_RE = /^G-[A-Z0-9]{4,16}$/;

function Panel({ title, right, children, pad = true }) {
  return (
    <div className="hq-panel" style={{ marginBottom: 16 }}>
      <div className="hq-panel-head"><h2>{title}</h2>{right}</div>
      {pad ? <div style={S.body}>{children}</div> : children}
    </div>
  );
}

function n(v) { return Number(v || 0).toLocaleString(); }
function change(now, before) {
  if (!before) return now ? 'new this period' : 'no change';
  const pct = Math.round(((now - before) / before) * 100);
  return (pct > 0 ? '+' : '') + pct + '% vs previous ' ;
}
function shortDay(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function when(s) { try { return new Date(s).toLocaleString(); } catch (e) { return '—'; } }

function DailyBars({ daily }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...daily.map((d) => d.views));
  const shown = hover != null ? daily[hover] : null;
  return (
    <div>
      <div style={{ ...S.muted, minHeight: 20, marginBottom: 8 }}>
        {shown
          ? <span><strong style={{ color: 'var(--hq-txt)' }}>{shortDay(shown.day)}</strong> · {n(shown.views)} views · {n(shown.visitors)} visitors</span>
          : 'Hover a bar for that day'}
      </div>
      <div role="img" aria-label="Page views per day"
        style={{ display: 'flex', alignItems: 'flex-end', gap: daily.length > 120 ? 0 : 2, height: 140, borderBottom: '1px solid var(--hq-line)' }}
        onMouseLeave={() => setHover(null)}>
        {daily.map((d, i) => (
          <div key={d.day} onMouseEnter={() => setHover(i)} title={`${shortDay(d.day)}: ${d.views} views, ${d.visitors} visitors`}
            style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}>
            <div style={{
              width: '100%', height: d.views ? Math.max(2, (d.views / max) * 100) + '%' : 0,
              background: BAR, borderRadius: '4px 4px 0 0', opacity: hover == null || hover === i ? 1 : 0.55,
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...S.muted, fontSize: 12, marginTop: 6 }}>
        <span>{daily.length ? shortDay(daily[0].day) : ''}</span>
        <span>peak {n(max)} views/day</span>
        <span>{daily.length ? shortDay(daily[daily.length - 1].day) : ''}</span>
      </div>
    </div>
  );
}

function Table({ head, rows, empty, cols }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="hq-table">
        <thead><tr>{head.map((h, i) => <th key={h} style={i >= cols ? S.num : undefined}>{h}</th>)}</tr></thead>
        <tbody>
          {rows}
          {(!rows || rows.length === 0) && <tr><td className="hq-empty" colSpan={head.length}>{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function WebsiteTraffic({ gaId, onGaChange }) {
  const [days, setDays] = useState(30);
  const [surface, setSurface] = useState('all');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    setErr('');
    apiFetch(`/api/site/analytics?days=${days}&surface=${surface}`)
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok) { setErr(d.error || 'Could not load traffic.'); return; }
        setData(d);
      })
      .catch(() => { if (live) setErr('Could not reach the server.'); });
    return () => { live = false; };
  }, [days, surface]);

  const ga = (gaId || '').trim().toUpperCase();
  const gaBad = ga !== '' && !GA_RE.test(ga);
  const devTotal = data && data.devices ? data.devices.reduce((a, d) => a + d.views, 0) : 0;

  return (
    <div>
      <Panel title="Google Analytics">
        <label style={S.label}>GA4 measurement ID
          <input className="hq-input" style={{ width: '100%', maxWidth: 320, boxSizing: 'border-box' }} maxLength={20}
            placeholder="G-XXXXXXXXXX" value={gaId || ''} onChange={(e) => onGaChange(e.target.value)} />
        </label>
        {gaBad && <p style={{ ...S.hint, color: '#e8c15a' }}>That doesn’t look like a GA4 ID (G- followed by letters and numbers). It won’t be saved.</p>}
        <p style={S.hint}>
          Loads Google Analytics on the live xyzmerch.com pages (home, careers, job posts). Never on previews, HQ or vendor links —
          a vendor link’s address is its password, so it stays out of Google. Save draft, then Publish to apply. Leave blank to turn it off.
          {data && data.ready && <> Live right now: <strong style={{ color: 'var(--hq-txt)' }}>{data.ga_live || 'off'}</strong>.</>}
          {' '}Reports: <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" style={LINK}>analytics.google.com</a>.
        </p>
      </Panel>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <select className="hq-select" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Date range">
          {RANGES.map(([v, l]) => <option key={v} value={v}>Last {l}</option>)}
        </select>
        <select className="hq-select" value={surface} onChange={(e) => setSurface(e.target.value)} aria-label="Pages">
          {SURFACES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {data && data.ready && <span style={S.muted}>Days are counted in {data.tz.replace('_', ' ')} time.</span>}
      </div>

      {err && <div className="hq-panel" style={{ padding: 16, marginBottom: 16, color: '#ff8a8a' }}>{err}</div>}
      {!err && !data && <div className="hq-panel" style={{ padding: 16, ...S.muted }}>Loading…</div>}
      {data && data.ready === false && (
        <div className="hq-panel" style={{ padding: 16, ...S.muted }}>
          Traffic tracking isn’t set up in the database yet. Apply <code>server/db/migrations/2026-10-15-site-analytics.sql</code>, then deploy.
        </div>
      )}

      {data && data.ready && (
        <>
          <div className="hq-cards">
            <div className="hq-stat"><div className="hq-k">Page views</div><div className="hq-v">{n(data.totals.views)}</div>
              <div style={{ ...S.muted, marginTop: 6 }}>{change(data.totals.views, data.previous.views)}{data.previous.views ? `${days} days` : ''}</div></div>
            <div className="hq-stat"><div className="hq-k">Visitors</div><div className="hq-v">{n(data.totals.visitors)}</div>
              <div style={{ ...S.muted, marginTop: 6 }}>Each browser counted once per day</div></div>
            <div className="hq-stat"><div className="hq-k">From other sites</div>
              <div className="hq-v">{n(data.referrers.reduce((a, r) => a + r.views, 0))}</div>
              <div style={{ ...S.muted, marginTop: 6 }}>Views with an outside referrer</div></div>
          </div>

          <Panel title="Page views per day"><DailyBars daily={data.daily} /></Panel>

          <div style={S.grid}>
            <Panel title="Top pages" pad={false}>
              <Table head={['Page', 'Views', 'Visitors']} cols={1} empty="No views in this range."
                rows={data.pages.map((p) => (
                  <tr key={p.surface + p.path}>
                    <td>{p.surface === 'vendor' ? 'Vendor links (all)' : p.path}
                      {p.surface === 'vendor' && <div style={{ ...S.muted, fontSize: 12 }}>/public/vendor/…</div>}</td>
                    <td style={S.num}>{n(p.views)}</td><td style={S.num}>{n(p.visitors)}</td>
                  </tr>))} />
            </Panel>

            <Panel title="Referrers" pad={false}>
              <Table head={['Site', 'Views', 'Visitors']} cols={1} empty="No outside referrers yet. Direct visits and links from apps often carry none."
                rows={data.referrers.map((r) => (
                  <tr key={r.host}>
                    <td>{r.host}
                      {r.top_url && <div style={{ fontSize: 12 }}><a href={r.top_url} target="_blank" rel="noreferrer noopener" style={LINK}>{r.top_url.replace(/^https?:\/\//, '').slice(0, 60)}</a></div>}</td>
                    <td style={S.num}>{n(r.views)}</td><td style={S.num}>{n(r.visitors)}</td>
                  </tr>))} />
            </Panel>

            <Panel title="Campaigns (utm tags)" pad={false}>
              <Table head={['Source / medium', 'Campaign', 'Views']} cols={2}
                empty="No tagged links yet. Add ?utm_source=instagram&utm_campaign=hiring to a link to see it here."
                rows={data.campaigns.map((c, i) => (
                  <tr key={i}>
                    <td>{c.source || '—'}{c.medium ? <span style={S.muted}> / {c.medium}</span> : ''}</td>
                    <td>{c.campaign || '—'}</td><td style={S.num}>{n(c.views)}</td>
                  </tr>))} />
            </Panel>

            <Panel title="Devices" pad={false}>
              <Table head={['Device', 'Views', 'Share']} cols={1} empty="No views in this range."
                rows={data.devices.map((d) => (
                  <tr key={d.device}>
                    <td style={{ textTransform: 'capitalize' }}>{d.device}</td>
                    <td style={S.num}>{n(d.views)}</td>
                    <td style={S.num}>{devTotal ? Math.round((d.views / devTotal) * 100) + '%' : '—'}</td>
                  </tr>))} />
            </Panel>
          </div>

          {surface !== 'site' && (
            <Panel title="Vendor link views" pad={false}>
              <Table head={['Vendor', 'Last opened', 'Views', 'Visitors']} cols={2} empty="No vendor has opened their link in this range."
                rows={data.vendors.map((v) => (
                  <tr key={v.id}>
                    <td>{v.name}</td><td style={S.muted}>{when(v.last_view)}</td>
                    <td style={S.num}>{n(v.views)}</td><td style={S.num}>{n(v.visitors)}</td>
                  </tr>))} />
            </Panel>
          )}

          <p style={{ ...S.hint, marginBottom: 16 }}>
            Counted here: real browsers loading a page (bots, link previews and form-result reloads are skipped). No IP addresses are stored, and rows older than 400 days are removed automatically.
            Google Analytics counts differently (it needs JavaScript and is blocked by many ad blockers), so its numbers will be lower.
          </p>
        </>
      )}
    </div>
  );
}
